import { useState, useEffect, useRef, useMemo, Fragment } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal } from 'react-bootstrap';
import Select from 'react-select';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { QrReader } from 'react-qr-reader';

import { setAlert } from '../../actions/alert';
import { confirmarReservacion, limpiarEscaner } from '../../actions/eventos';

const EscanearEntrada = ({ entrada, reestablecerEntrada }) => {

    const dispatch =  useDispatch();

    const { escaner } = useSelector(state => state.eventos);

    const [escanearModal, setEscanearModal] = useState(false);
    const [statusProcesar, setStatusProcesar] = useState(false);

    const [entradaData, setEntradaData] = useState({
        id: null, 
        codigo: null
    });

    const [camarasFilter, setCamarasFilter] = useState([]);
    const [deviceId, setDeviceId] = useState(null);
    const [videoConstraints, setVideoConstraints] = useState(null);
    const [readerKey, setReaderKey] = useState(0);

    const lastResult = useRef();

    /* Escanear entrada */
    const onReadResult = (result, error) => {
        if (!result) return;

        if (lastResult.current === result.text) {
            return;
        }
        
        lastResult.current = result.text;

        setEntradaData({
            ...entradaData, 
            codigo: result.text
        });
    }

    const handleInputChange = ({ target }) => {
        setEntradaData({
            ...entradaData,
            [target.name]: target.value
        });
    }

    const handleHabilitarCamaras = async () => {
        try {
            if (!window.isSecureContext) {
                alert('Esta página no está en HTTPS; no se listarán cámaras en móvil.');
                return;
            }

            // Pide permiso con la trasera idealmente
            const tempStream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: 'environment' }
            });

            // Enumera
            const devices = await navigator.mediaDevices.enumerateDevices();
            const videos = devices.filter(d => d.kind === 'videoinput');

            setCamarasFilter(
                videos.map(c => ({
                    value: c.deviceId,
                    label: c.label || `Cámara ${c.deviceId.slice(0, 6)}…`
                }))
            );

            // Preferir back si existe
            const back = videos.find(d => /back|environment|trás|trasera|posterior/i.test(d.label));
            const preferida = back?.deviceId ?? videos[0]?.deviceId ?? null;
            setDeviceId(preferida);

            // MUY IMPORTANTE: liberar el stream temporal
            tempStream.getTracks().forEach(t => t.stop());
        } catch (e) {
            console.error(e);
            setCamarasFilter([]);
            setDeviceId(null);
        }
    }

    const handleSelectCamara = (option) => {
        setDeviceId(option);
        lastResult.current = undefined;
        setReaderKey(k => k + 1);
    }

    const handleProcesar = async () => {
        if(entradaData.codigo){
            await setStatusProcesar(true);
            await dispatch(confirmarReservacion(entradaData));
            await setStatusProcesar(false);
        }else{
            await dispatch(setAlert('Completa todos los campos', 'danger'));
        }
    }

    const handleCerrar = () => {
        setReaderKey(k => k + 1);
        window.location.reload();
    }

    useEffect(() => {
        if(entrada){
            setEscanearModal(true);

            setEntradaData({
                ...entradaData, 
                id: entrada.id, 
                codigo: null
            });
        }        
    }, [entrada]);

    useEffect(() => {
        if(escaner){
            dispatch(limpiarEscaner());

            setTimeout(() => {
                handleCerrar();
            }, 2000);
        }        
    }, [escaner]);

    useEffect(() => {
        const initCamaras = async () => {
            if (!escanearModal) return;

            try {
                // iOS/Android a veces no dan labels sin pedir permiso primero:
                await navigator.mediaDevices.getUserMedia({ video: true });
                const devices = await navigator.mediaDevices.enumerateDevices();
                const videos = devices.filter(d => d.kind === 'videoinput');

                setCamarasFilter(
                    videos.map(c => ({
                        value: c.deviceId,
                        label: c.label || `Cámara ${c.deviceId.slice(0, 6)}…`
                    }))
                );

                // Por defecto: la “trasera” si existe, si no la primera
                // Muchos navegadores marcan la trasera con label que incluye 'back' o 'environment'
                const back = videos.find(d =>
                    /back|environment|trás|trasera|posterior/i.test(d.label)
                );

                const preferida = back?.deviceId ?? videos[0]?.deviceId ?? null;

                setDeviceId(prev => prev ?? preferida);
            } catch (e) {
                console.error(e);
                setCamarasFilter([]);
                setDeviceId(null);
            }
        };

        initCamaras();
    }, [escanearModal]);

    useEffect(() => {
        setVideoConstraints(deviceId?.value
            ? { deviceId: { exact: deviceId?.value } }
            : { facingMode: 'environment' }
        );
    }, [deviceId]);

    return (
        <Fragment>
            <HelmetProvider>
                <Helmet>
                    <style>{`
                        input[type="file"] {
                            display: none;
                        }
                        
                        .custom-file-upload {
                            border-radius: 5%;
                            display: inline-block;
                            position: relative;
                            padding: 0px;
                            cursor: pointer;
                            background: #fff;
                            margin-bottom: 25px;
                            border: 5px solid #111F62;
                        }
                        
                        .img-wrap{
                            position: relative;
                            //width: 200px;
                            height: 200px;
                            overflow: hidden;
                            border-radius: 5%;
                        }

                        .img-upload:before{
                            content: "";
                            font-family: "Font Awesome 6 Free" !important;
                            font-weight: 900;
                            font-size: 90px;
                            position: absolute;
                            padding-top: 50px;
                            top: 50%;
                            left: 50%;
                            transform: translate(-50%, -50%);
                            text-align: center;
                            color: #21B4E9;
                            width: 100%;
                            height: 200px;
                            border-radius: 5%;
                            opacity: 0;
                            transition: .5s ease;
                            background-color: #fff;
                        }

                        .img-upload:hover:before{
                            opacity: 1;
                        }

                        #img-photo {
                            width: auto;
                            height: 100%;
                        }

                        .selectIconos, .select-group {
                            height: 100%;
                        }

                        .delete {
                            padding: 0.39rem 0.70rem !important;
                            border-radius: 0 25% 25% 0 !important;
                            height: 100%;
                        }

                        #label_limite_check:before {
                            content: "";
                            display: inline-block;
                            height: 100%;
                            vertical-align: middle;
                        }
                    `}</style>
                </Helmet>
            </HelmetProvider>
            
            <Modal show={escanearModal} size="md" backdrop="static" centered onHide={() => handleCerrar()}>
                <Modal.Header closeButton>
                    <Modal.Title>Escanear reservación</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="row">
                        {
                            camarasFilter.length > 0 && (
                                <div className="col-12 form-group text-center">
                                    <QrReader
                                        key={readerKey}
                                        onResult={onReadResult}
                                        constraints={videoConstraints}
                                        videoContainerStyle={{ width: '100%' }}
                                        videoStyle={{ width: '100%' }}
                                        videoId="modelvideo" 
                                        style={{ width: '100%' }}
                                    />
                                </div>
                            )
                        }

                        {
                            camarasFilter.length > 0 && (
                                <div className="col-md-12 form-group my-3">
                                    <div className="input-group h-100">
                                        <Select
                                            id="deviceId"
                                            name="deviceId"
                                            className="select-group"
                                            classNamePrefix="react-select"
                                            placeholder="Cámara a usar"
                                            options={camarasFilter}
                                            value={deviceId}
                                            onChange={(value) => handleSelectCamara(value)}
                                            menuPortalTarget={document.body}
                                            styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                        />
                                    </div>
                                </div>
                             )  
                        }

                        <div className="col-md-12 form-group my-3">
                            <div className="input-group">
                                <div className="form-floating">
                                    <input type="text" className="form-control" placeholder="Código de la reservación" id="codigo" name="codigo" value={entradaData.codigo} onChange={e => handleInputChange(e)}/>
                                    <label htmlFor="codigo">Código de la reservación</label>
                                </div>
                            </div>
                        </div>
                    </div>                    
                </Modal.Body>
                <Modal.Footer>
                    {
                        camarasFilter.length == 0 && (
                            <button type="button" className="btn btn-primary" onClick={() => handleHabilitarCamaras()}><i className="fas fa-camera fa-sm"></i> Mostrar cámaras</button>
                        )
                    }

                    {
                        statusProcesar ?
                            <button type="button" className="btn btn-success" disabled><span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> Aceptar</button>
                        :
                            <button type="button" className="btn btn-success" onClick={() => handleProcesar()}><i className="fas fa-check fa-sm"></i> Aceptar</button>
                    }
                </Modal.Footer>
            </Modal>
        </Fragment>
    )
}

export default EscanearEntrada;